import React, { useState, useCallback, useMemo } from 'react';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  LineChart, Line,
} from 'recharts';
import { saveISAK } from '../api';

// ─── Colores ───────────────────────────────────────────────────────────────────
const COLORES_MOLECULAR = ['#e74c3c', '#3498db'];
const COLORES_TISULAR   = ['#f39c12', '#e74c3c', '#95a5a6', '#2ecc71'];
const COLORES_DIST_GRASA = ['#e67e22', '#27ae60'];

// ─── Helpers ───────────────────────────────────────────────────────────────────
const n = (v) => parseFloat(v) || 0;
const r2 = (v) => Math.round(v * 100) / 100;

function semaforo(valor, rangos) {
  for (const { min, max, color, clasificacion } of rangos) {
    if ((min === undefined || valor >= min) && (max === undefined || valor < max)) {
      return { color, clasificacion };
    }
  }
  return { color: '#95a5a6', clasificacion: '-' };
}

const RANGOS_IMC = [
  { max: 18.5, color: '#f39c12', clasificacion: 'Bajo peso' },
  { min: 18.5, max: 25,   color: '#27ae60', clasificacion: 'Normal' },
  { min: 25,   max: 30,   color: '#e74c3c', clasificacion: 'Sobrepeso' },
  { min: 30,              color: '#c0392b', clasificacion: 'Obesidad' },
];
const RANGOS_CC = [
  { max: 1.0,  color: '#27ae60', clasificacion: 'Bajo riesgo de enfermedades crónicas' },
  { min: 1.0,  color: '#e74c3c', clasificacion: 'Riesgo aumentado' },
];
const RANGOS_CT = [
  { max: 0.50, color: '#27ae60', clasificacion: 'Bajo riesgo de enfermedades crónicas' },
  { min: 0.50, color: '#e74c3c', clasificacion: 'Riesgo aumentado' },
];
const RANGOS_CON = [
  { max: 1.25,              color: '#27ae60', clasificacion: 'Riesgo cardiovascular bajo' },
  { min: 1.25, max: 1.40,  color: '#f39c12', clasificacion: 'Riesgo cardiovascular moderado' },
  { min: 1.40,              color: '#e74c3c', clasificacion: 'Riesgo cardiovascular alto' },
];
const RANGOS_AM = [
  { max: 0.7,  color: '#27ae60', clasificacion: 'Bajo' },
  { min: 0.7, max: 1.0, color: '#f39c12', clasificacion: 'Aceptable' },
  { min: 1.0,  color: '#e74c3c', clasificacion: 'Alto' },
];
const RANGOS_MO = [
  { max: 2.34,              color: '#e74c3c', clasificacion: 'Muy bajo' },
  { min: 2.34, max: 2.44,  color: '#f39c12', clasificacion: 'Bajo' },
  { min: 2.44, max: 3.11,  color: '#27ae60', clasificacion: 'Medio' },
  { min: 3.11, max: 3.29,  color: '#2980b9', clasificacion: 'Alto' },
  { min: 3.29,              color: '#1a5276', clasificacion: 'Muy alto' },
];

// ─── Motor de calculos ISAK ────────────────────────────────────────────────────
function calcularISAK(d) {
  const masa    = n(d.masa_corporal);
  const talla   = n(d.talla);          // cm
  const tallaM  = talla / 100;

  // Pliegues
  const tri  = n(d.triceps);
  const sub  = n(d.subescapular);
  const bic  = n(d.biceps);
  const ci   = n(d.cresta_iliaca);
  const sup  = n(d.supraespinal);
  const abd  = n(d.abdominal);
  const mus  = n(d.muslo);
  const pie  = n(d.pierna);

  // Perimetros
  const brazoRelaj  = n(d.brazo_relajado);
  const brazoFlex   = n(d.brazo_flexionado);
  const cintura     = n(d.cintura);
  const caderas     = n(d.caderas);
  const musloMed    = n(d.muslo_medio);
  const piernaP     = n(d.pierna_perimetro);

  // Diametros
  const humero      = n(d.humero);
  const femur       = n(d.femur);

  const sexo  = d.sexo || 'M';
  const edad  = n(d.edad);

  // Suma pliegues
  const suma6 = tri + sub + bic + ci + sup + abd;
  const suma8 = suma6 + mus + pie;
  const suma4DW = bic + tri + sub + sup; // Durnin-Womersley

  // Perimetros corregidos
  const brazoCor  = brazoRelaj - Math.PI * (tri / 10);
  const musloCor  = musloMed   - Math.PI * (mus / 10);
  const piernaCor = piernaP    - Math.PI * (pie / 10);

  // IMC
  const imc = tallaM > 0 ? r2(masa / (tallaM * tallaM)) : 0;

  // Densidad corporal - Durnin & Womersley (1974)
  let Db = 0;
  if (suma4DW > 0 && edad > 0) {
    const logS = Math.log10(suma4DW);
    if (sexo === 'M') {
      // Hombres 17-72 (ecuacion general)
      Db = 1.1765 - 0.0744 * logS;
    } else {
      Db = 1.1567 - 0.0717 * logS;
    }
  }
  const pctGrasa    = Db > 0 ? r2((4.95 / Db - 4.50) * 100) : 0;
  const masaGrasa   = r2(masa * pctGrasa / 100);
  const masaLibre   = r2(masa - masaGrasa);

  // Masa osea - Rocha (1974): MO = 3.02 * (H² × Df × Dh × 400)^0.712
  // H en metros, diametros en metros (cm / 100)
  const masaOsea = (tallaM > 0 && femur > 0 && humero > 0)
    ? r2(3.02 * Math.pow(tallaM * tallaM * (femur / 100) * (humero / 100) * 400, 0.712))
    : 0;

  // Masa muscular - Lee (2000)
  const sexoNum = sexo === 'M' ? 1 : 0;
  const masaMuscular = (tallaM > 0)
    ? r2(tallaM * (0.00744 * brazoCor * brazoCor + 0.00088 * musloCor * musloCor + 0.00441 * piernaCor * piernaCor)
        + 2.4 * sexoNum - 0.048 * edad + 7.8)
    : 0;

  // Tejido adiposo - Kerr (1991) aproximacion
  const tejidoAdiposo = r2(masaGrasa * 1.25);
  const tejidoResidual = r2(Math.max(0, masa - masaOsea - masaMuscular - tejidoAdiposo));

  // Indices composicion corporal
  const indAdiposoMuscular = masaMuscular > 0 ? r2(tejidoAdiposo / masaMuscular) : 0;
  const indMusculoOseo     = masaOsea > 0     ? r2(masaMuscular / masaOsea)      : 0;

  // Indices de salud
  const indCC      = caderas > 0 ? r2(cintura / caderas)   : 0;
  const indCT      = talla   > 0 ? r2(cintura / talla)     : 0;
  const indConicidad = (masa > 0 && tallaM > 0)
    ? r2(cintura / 100 / (0.109 * Math.sqrt(masa / tallaM)))
    : 0;

  // Indice distribucion grasa
  const plieguesTronco = sub + ci + sup + abd;
  const plieguesExtrem = tri + bic + mus + pie;
  const idg = plieguesTronco > 0 ? r2(plieguesExtrem / plieguesTronco) : 0;
  const pctTronco  = suma8 > 0 ? r2(plieguesTronco / suma8 * 100) : 0;
  const pctExtrem  = suma8 > 0 ? r2(plieguesExtrem / suma8 * 100) : 0;

  // Somatotipo - Carter & Heath
  let endomorfia = 0, mesomorfia = 0, ectomorfia = 0;
  if (talla > 0) {
    const suma3corr = (tri + sub + sup) * (170.18 / talla);
    endomorfia = r2(-0.7182 + 0.1451 * suma3corr - 0.00068 * suma3corr * suma3corr + 0.0000014 * Math.pow(suma3corr, 3));
    mesomorfia = r2(0.858 * humero + 0.601 * femur + 0.188 * brazoCor + 0.161 * piernaCor - 0.131 * talla + 4.5);
    const hwr = talla / Math.cbrt(masa);
    if (hwr >= 40.75)     ectomorfia = r2(0.732 * hwr - 28.58);
    else if (hwr >= 38.25) ectomorfia = r2(0.463 * hwr - 17.63);
    else                   ectomorfia = 0.1;
  }
  const somaX = r2(ectomorfia - endomorfia);
  const somaY = r2(2 * mesomorfia - (endomorfia + ectomorfia));

  return {
    suma6, suma8,
    imc, pctGrasa, masaGrasa, masaLibre,
    masaOsea, masaMuscular, tejidoAdiposo, tejidoResidual,
    brazoCor: r2(brazoCor), musloCor: r2(musloCor), piernaCor: r2(piernaCor),
    indAdiposoMuscular, indMusculoOseo,
    indCC, indCT, indConicidad, idg,
    pctTronco, pctExtrem,
    endomorfia, mesomorfia, ectomorfia,
    somaX, somaY,
    difBrazo: r2(brazoFlex - brazoRelaj),
  };
}

// ─── Somatotipo SVG ────────────────────────────────────────────────────────────
const SomatotipoChart = React.memo(function SomatotipoChart({ endomorfia, mesomorfia, ectomorfia, somaX, somaY }) {
  // Triangulo equilatero centrado en SVG 300x260
  const cx = 150, cy = 130, scale = 15;
  const px = cx + somaX * scale;
  const py = cy - somaY * scale;
  return (
    <svg viewBox="0 0 300 260" style={{ width: '100%', maxWidth: 320 }}>
      {/* Grid lines */}
      {[-8,-6,-4,-2,0,2,4,6,8].map(v => (
        <line key={v} x1={cx + v * scale} y1={20} x2={cx + v * scale} y2={240} stroke="#eee" strokeWidth="1"/>
      ))}
      {[-8,-6,-4,-2,0,2,4,6,8,10,12].map(v => (
        <line key={v} x1={10} y1={cy - v * scale} x2={290} y2={cy - v * scale} stroke="#eee" strokeWidth="1"/>
      ))}
      {/* Ejes */}
      <line x1={cx} y1={20} x2={cx} y2={240} stroke="#bbb" strokeWidth="1.5"/>
      <line x1={10} y1={cy} x2={290} y2={cy} stroke="#bbb" strokeWidth="1.5"/>
      {/* Triangulo */}
      <polygon
        points={`${cx},${cy - 12 * scale} ${cx - 8 * scale},${cy + 8 * scale} ${cx + 8 * scale},${cy + 8 * scale}`}
        fill="none" stroke="#2980b9" strokeWidth="1.5" strokeDasharray="5,3"
      />
      {/* Labels vertices */}
      <text x={cx} y={cy - 12 * scale - 8} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#2c3e50">Mesomorfia</text>
      <text x={cx - 8 * scale - 5} y={cy + 8 * scale + 14} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#2c3e50">Endomorfia</text>
      <text x={cx + 8 * scale + 5} y={cy + 8 * scale + 14} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#2c3e50">Ectomorfia</text>
      {/* Punto actual */}
      {(endomorfia > 0 || mesomorfia > 0 || ectomorfia > 0) && (
        <>
          <circle cx={px} cy={py} r={6} fill="#27ae60" stroke="#fff" strokeWidth="2"/>
          <text x={px + 9} y={py + 4} fontSize="10" fill="#27ae60">Actual</text>
        </>
      )}
    </svg>
  );
});

// ─── Fila de indice con semaforo ───────────────────────────────────────────────
function FilaIndice({ label, valor, rangos, rango_texto, interpretacion }) {
  const { color, clasificacion } = semaforo(valor, rangos);
  return (
    <div className="isak-indice-fila">
      <div className="isak-indice-info">
        <span className="isak-indice-label">{label}</span>
        {interpretacion && <span className="isak-indice-interp">{interpretacion}</span>}
      </div>
      <div className="isak-indice-derecha">
        <span className="isak-indice-valor" style={{ background: color }}>{valor}</span>
        <span className="isak-indice-clase">{clasificacion}</span>
        {rango_texto && <span className="isak-indice-rango">Saludable: {rango_texto}</span>}
      </div>
    </div>
  );
}

// ─── Secciones de resultados con graficos, memoizadas ──────────────────────────
// Reciben valores primitivos (no el objeto `calc` completo) para que React.memo
// pueda evitar re-renderizar graficos de Recharts cuando el usuario tipea en un
// campo que no afecta esos valores especificos.
const ComposicionCorporalSection = React.memo(function ComposicionCorporalSection({
  masaGrasa, masaLibre, tejidoAdiposo, masaMuscular, masaOsea, tejidoResidual, masaCorporal,
}) {
  const dataMolecular = useMemo(() => [
    { name: 'Masa grasa',        value: masaGrasa  || 0 },
    { name: 'Masa libre de grasa', value: masaLibre || 0 },
  ], [masaGrasa, masaLibre]);
  const dataTisular = useMemo(() => [
    { name: 'Tej. adiposo',  value: tejidoAdiposo  || 0 },
    { name: 'Tej. muscular', value: masaMuscular   || 0 },
    { name: 'Tej. óseo',     value: masaOsea       || 0 },
    { name: 'Residual',      value: tejidoResidual || 0 },
  ], [tejidoAdiposo, masaMuscular, masaOsea, tejidoResidual]);
  const dataMolBar = useMemo(() => [
    { name: 'Masa grasa',    val: masaGrasa  || 0 },
    { name: 'Masa lib. grasa', val: masaLibre || 0 },
    { name: 'Masa corporal', val: masaCorporal },
  ], [masaGrasa, masaLibre, masaCorporal]);
  const dataTisBar = useMemo(() => [
    { name: 'Tej. adiposo',  val: tejidoAdiposo || 0 },
    { name: 'Tej. muscular', val: masaMuscular  || 0 },
  ], [tejidoAdiposo, masaMuscular]);

  return (
    <div className="isak-seccion isak-resultados">
      <h4 className="isak-seccion-titulo isak-titulo-resultado">Composición Corporal</h4>

      <div className="isak-subseccion-titulo">Fraccionamiento molecular</div>
      <div className="isak-tabla-header"><span>Medida</span><span>Resultado</span></div>
      <div className="isak-tabla-fila isak-calculado"><span>Masa grasa (kg)</span><span>{masaGrasa || 0}</span></div>
      <div className="isak-tabla-fila isak-calculado"><span>Masa libre de grasa (kg)</span><span>{masaLibre || 0}</span></div>

      <div className="isak-subseccion-titulo" style={{ marginTop: 12 }}>Fraccionamiento tisular</div>
      <div className="isak-tabla-header"><span>Medida</span><span>Resultado</span></div>
      <div className="isak-tabla-fila isak-calculado"><span>Tejido adiposo (kg)</span><span>{tejidoAdiposo || 0}</span></div>
      <div className="isak-tabla-fila isak-calculado"><span>Tejido muscular (kg)</span><span>{masaMuscular || 0}</span></div>
      <div className="isak-tabla-fila isak-calculado"><span>Tejido óseo (kg)</span><span>{masaOsea || 0}</span></div>
      <div className="isak-tabla-fila isak-calculado"><span>Tejido residual (kg)</span><span>{tejidoResidual || 0}</span></div>

      {/* Graficos composicion */}
      <div className="isak-graficos-row">
        <div className="isak-grafico-bloque">
          <p className="isak-grafico-titulo">Fraccionamiento molecular</p>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={dataMolecular} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ percent }) => `${(percent*100).toFixed(1)}%`}>
                {dataMolecular.map((_, i) => <Cell key={i} fill={COLORES_MOLECULAR[i]} />)}
              </Pie>
              <Tooltip formatter={(v) => `${v} kg`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="isak-grafico-bloque">
          <p className="isak-grafico-titulo">Fraccionamiento tisular</p>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={dataTisular} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ percent }) => `${(percent*100).toFixed(1)}%`}>
                {dataTisular.map((_, i) => <Cell key={i} fill={COLORES_TISULAR[i]} />)}
              </Pie>
              <Tooltip formatter={(v) => `${v} kg`} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="isak-graficos-row">
        <div className="isak-grafico-bloque">
          <p className="isak-grafico-titulo">Comparativa molecular (kg)</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={dataMolBar}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="val" fill="#3498db" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="isak-grafico-bloque">
          <p className="isak-grafico-titulo">Comparativa tisular (kg)</p>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={dataTisBar}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="val" fill="#f39c12" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
});

const AdiposidadSection = React.memo(function AdiposidadSection({
  suma6, suma8, triceps, subescapular, biceps, cresta_iliaca, supraespinal, abdominal, muslo, pierna,
}) {
  const dataPliegues = useMemo(() => [
    { name: 'Tríceps',      val: n(triceps) },
    { name: 'Subesc.',      val: n(subescapular) },
    { name: 'Bíceps',       val: n(biceps) },
    { name: 'Cr. Ilíaca',   val: n(cresta_iliaca) },
    { name: 'Supraespin.',  val: n(supraespinal) },
    { name: 'Abdominal',    val: n(abdominal) },
    { name: 'Muslo',        val: n(muslo) },
    { name: 'Pierna',       val: n(pierna) },
  ], [triceps, subescapular, biceps, cresta_iliaca, supraespinal, abdominal, muslo, pierna]);
  const dataSuma8 = useMemo(() => [{ name: 'Suma 8', val: suma8 || 0 }], [suma8]);

  return (
    <div className="isak-seccion isak-resultados">
      <h4 className="isak-seccion-titulo isak-titulo-resultado">Adiposidad</h4>
      <div className="isak-tabla-fila isak-calculado"><span>Sumatorio de 6 pliegues (mm)</span><span>{suma6 || 0}</span></div>
      <div className="isak-tabla-fila isak-calculado"><span>Sumatorio de 8 pliegues (mm)</span><span>{suma8 || 0}</span></div>
      <div className="isak-graficos-row">
        <div className="isak-grafico-bloque">
          <p className="isak-grafico-titulo">Pliegues individuales (mm)</p>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={dataPliegues}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="val" stroke="#27ae60" strokeWidth={2} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="isak-grafico-bloque">
          <p className="isak-grafico-titulo">Sumatorio 8 pliegues (mm)</p>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={dataSuma8}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="val" fill="#27ae60" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
});

const MuscularidadSection = React.memo(function MuscularidadSection({
  brazoCor, musloCor, piernaCor, difBrazo,
}) {
  const dataPerCor = useMemo(() => [
    { name: 'Brazo',  val: brazoCor  || 0 },
    { name: 'Muslo',  val: musloCor  || 0 },
    { name: 'Pierna', val: piernaCor || 0 },
  ], [brazoCor, musloCor, piernaCor]);

  return (
    <div className="isak-seccion isak-resultados">
      <h4 className="isak-seccion-titulo isak-titulo-resultado">Muscularidad</h4>
      <div className="isak-tabla-header"><span>Medida</span><span>Resultado</span></div>
      <div className="isak-tabla-fila isak-calculado"><span>Brazo corregido (cm)</span><span>{brazoCor || 0}</span></div>
      <div className="isak-tabla-fila isak-calculado"><span>Muslo corregido (cm)</span><span>{musloCor || 0}</span></div>
      <div className="isak-tabla-fila isak-calculado"><span>Pierna corregida (cm)</span><span>{piernaCor || 0}</span></div>
      <div className="isak-tabla-fila isak-calculado">
        <span>Dif. brazo flexionado - brazo relajado (cm)</span><span>{difBrazo || 0}</span>
      </div>
      <div className="isak-grafico-bloque" style={{ marginTop: 12 }}>
        <p className="isak-grafico-titulo">Perímetros corregidos (cm)</p>
        <ResponsiveContainer width="100%" height={180}>
          <BarChart data={dataPerCor}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="val" fill="#2980b9" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});

const SomatotipoSection = React.memo(function SomatotipoSection({
  endomorfia, mesomorfia, ectomorfia, somaX, somaY,
}) {
  return (
    <div className="isak-seccion isak-resultados">
      <h4 className="isak-seccion-titulo isak-titulo-resultado">Somatotipo</h4>
      <div className="isak-tabla-header"><span>Componente</span><span>Valor</span></div>
      <div className="isak-tabla-fila isak-calculado"><span>Endomorfia</span><span>{endomorfia || 0}</span></div>
      <div className="isak-tabla-fila isak-calculado"><span>Mesomorfia</span><span>{mesomorfia || 0}</span></div>
      <div className="isak-tabla-fila isak-calculado"><span>Ectomorfia</span><span>{ectomorfia || 0}</span></div>
      <div style={{ marginTop: 16 }}>
        <SomatotipoChart
          endomorfia={endomorfia || 0}
          mesomorfia={mesomorfia || 0}
          ectomorfia={ectomorfia || 0}
          somaX={somaX || 0}
          somaY={somaY || 0}
        />
      </div>
    </div>
  );
});

const IndicesSaludSection = React.memo(function IndicesSaludSection({
  indCC, indCT, indConicidad, imc, idg, pctTronco, pctExtrem,
}) {
  const dataDistGrasa = useMemo(() => [
    { name: 'Tronco',       value: pctTronco || 0 },
    { name: 'Extremidades', value: pctExtrem || 0 },
  ], [pctTronco, pctExtrem]);

  return (
    <div className="isak-seccion isak-resultados">
      <h4 className="isak-seccion-titulo isak-titulo-resultado">Índices de Salud</h4>
      <FilaIndice label="Índice cintura/cadera" valor={indCC || 0} rangos={RANGOS_CC} rango_texto="< 1,00"
        interpretacion="Bajo riesgo de desarrollo de enfermedades crónicas" />
      <FilaIndice label="Índice cintura/talla" valor={indCT || 0} rangos={RANGOS_CT} rango_texto="< 0,50"
        interpretacion="Bajo riesgo de desarrollo de enfermedades crónicas" />
      <FilaIndice label="Índice de conicidad" valor={indConicidad || 0} rangos={RANGOS_CON} rango_texto="< 1,4"
        interpretacion="Riesgo cardiovascular" />
      <FilaIndice label="IMC (kg/m²)" valor={imc || 0} rangos={RANGOS_IMC} rango_texto="18,5 - 24,9" />
      <div className="isak-tabla-fila isak-calculado" style={{ marginTop: 8 }}>
        <span>Índice de distribución grasa</span><span>{idg || 0}</span>
      </div>
      <p className="isak-indice-interp" style={{ marginTop: 4 }}>
        Un menor valor implica mayor acumulación de grasa en el tronco, lo que se relaciona con mayor riesgo de enfermedad metabólica.
      </p>

      <div className="isak-grafico-bloque" style={{ marginTop: 16 }}>
        <p className="isak-grafico-titulo">Distribución corporal de grasa (%)</p>
        <ResponsiveContainer width="100%" height={250}>
          <PieChart margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
            <Pie data={dataDistGrasa} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={65}
              label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}>
              {dataDistGrasa.map((_, i) => <Cell key={i} fill={COLORES_DIST_GRASA[i]} />)}
            </Pie>
            <Tooltip formatter={(v) => `${v}%`} />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
});

// ─── Componente principal ──────────────────────────────────────────────────────
const CAMPOS_INICIALES = {
  fecha_evaluacion: '', fecha_nacimiento: '', fecha_menstruacion: '',
  sexo: 'M', deporte: '', nivel_actividad: '', edad: '',
  masa_corporal: '', talla: '', talla_sentado: '', envergadura: '',
  triceps: '', subescapular: '', biceps: '', cresta_iliaca: '',
  supraespinal: '', abdominal: '', muslo: '', pierna: '',
  brazo_relajado: '', brazo_flexionado: '', cintura: '', caderas: '',
  muslo_medio: '', pierna_perimetro: '',
  humero: '', biastilodeo: '', femur: '',
};

function FichaISAK({ pacienteId, recordId, editable = true, onEditar, onCancelar, onGuardado, onEliminar, datosIniciales, paciente }) {
  const [campos, setCampos] = useState(() => {
    const base = { ...CAMPOS_INICIALES };

    // Autocompletar con datos del paciente
    if (paciente) {
      const hoy = new Date().toISOString().split('T')[0];
      if (!base.fecha_evaluacion) base.fecha_evaluacion = hoy;

      const fichaBirthDate = (paciente.camposExtra || []).find(c => c.field_id === 'birth_date');
      const fichaSex = (paciente.camposExtra || []).find(c => c.field_id === 'sex');

      if (fichaBirthDate?.value) {
        base.fecha_nacimiento = fichaBirthDate.value;
        const nacimiento = new Date(fichaBirthDate.value);
        const hoyDate = new Date();
        let edad = hoyDate.getFullYear() - nacimiento.getFullYear();
        const m = hoyDate.getMonth() - nacimiento.getMonth();
        if (m < 0 || (m === 0 && hoyDate.getDate() < nacimiento.getDate())) edad--;
        base.edad = String(edad);
      }
      if (fichaSex?.value) base.sexo = fichaSex.value;
    }

    if (datosIniciales && Object.keys(datosIniciales).length > 0) {
      return { ...base, ...datosIniciales };
    }
    return base;
  });
  const calc = useMemo(() => calcularISAK(campos), [campos]);
  const [guardado, setGuardado] = useState(false);
  const [cargando] = useState(false);

  // Los datos siempre se reciben desde el padre via datosIniciales

  const set = useCallback((key, val) => {
    if (!editable) return;
    setCampos(prev => ({ ...prev, [key]: val }));
  }, [editable]);

  const guardar = async () => {
    try {
      const actualizado = await saveISAK(pacienteId, recordId, campos);
      setGuardado(true);
      setTimeout(() => setGuardado(false), 2500);
      if (onGuardado) onGuardado(actualizado);
    } catch (err) {
      console.error('Error guardando ISAK:', err);
    }
  };

  if (cargando) return <p className="sesiones-vacio">Cargando evaluación ISAK...</p>;

  const inputNum = (key, label, unidad) => (
    <div className="isak-campo">
      <label className="isak-label">{label}</label>
      <div className="isak-input-wrap">
        <input
          type="number" step="0.1" className="isak-input"
          value={campos[key]}
          onChange={e => set(key, e.target.value)}
          disabled={!editable}
        />
        {unidad && <span className="isak-unidad">{unidad}</span>}
      </div>
    </div>
  );

  return (
    <div className="isak-wrapper">
      <div className="isak-titulo-header">
        <span></span>
        <div style={{ display: 'flex', gap: 8 }}>
          {!editable && onEditar && (
            <button className="btn-guardar-sesion" type="button" onClick={onEditar}>
              Editar
            </button>
          )}
          {editable && (
            <button className="btn-guardar-sesion" onClick={guardar} disabled={guardado}>
              {guardado ? 'Guardado ✓' : 'Guardar evaluación'}
            </button>
          )}
          {editable && onCancelar && (
            <button className="modal-btn-cancelar" type="button" onClick={onCancelar}>
              Cancelar
            </button>
          )}
          {onEliminar && (
            <button className="btn-eliminar-sesion" type="button" onClick={onEliminar}>
              Eliminar plantilla
            </button>
          )}
        </div>
      </div>

      {/* ── Datos generales ─────────────────────────────────────── */}
      <div className="isak-seccion">
        <h4 className="isak-seccion-titulo">Datos generales</h4>
        <div className="isak-grid">
          <div className="isak-campo">
            <label className="isak-label">Fecha de evaluación</label>
            <div className="isak-input-wrap">
              <input type="date" className="isak-input" value={campos.fecha_evaluacion} onChange={e => set('fecha_evaluacion', e.target.value)} disabled={!editable} />
              {editable && (
                <button type="button" className="recom-btn-hoy" onClick={() => set('fecha_evaluacion', new Date().toISOString().slice(0, 10))}>
                  Hoy
                </button>
              )}
            </div>
          </div>
          <div className="isak-campo">
            <label className="isak-label">Fecha de nacimiento</label>
            <input type="date" className="isak-input" value={campos.fecha_nacimiento} onChange={e => set('fecha_nacimiento', e.target.value)} disabled={!editable} />
          </div>
          {inputNum('edad', 'Edad', 'años')}
          <div className="isak-campo">
            <label className="isak-label">Sexo</label>
            <select className="isak-input" value={campos.sexo} onChange={e => set('sexo', e.target.value)} disabled={!editable}>
              <option value="M">Masculino</option>
              <option value="F">Femenino</option>
            </select>
          </div>
          <div className="isak-campo">
            <label className="isak-label">Deporte</label>
            <input type="text" className="isak-input" value={campos.deporte} onChange={e => set('deporte', e.target.value)} disabled={!editable} />
          </div>
          <div className="isak-campo">
            <label className="isak-label">Nivel de actividad</label>
            <input type="text" className="isak-input" value={campos.nivel_actividad} onChange={e => set('nivel_actividad', e.target.value)} disabled={!editable} />
          </div>
          {campos.sexo !== 'M' && (
            <div className="isak-campo">
              <label className="isak-label">Fecha de menstruación</label>
              <input type="date" className="isak-input" value={campos.fecha_menstruacion} onChange={e => set('fecha_menstruacion', e.target.value)} disabled={!editable} />
            </div>
          )}
        </div>
      </div>

      {/* ── Medidas basicas ──────────────────────────────────────── */}
      <div className="isak-seccion">
        <h4 className="isak-seccion-titulo">Medidas Básicas</h4>
        <div className="isak-tabla-header">
          <span>Medida</span><span>Resultado</span>
        </div>
        <div className="isak-tabla-fila">
          <span>Masa corporal (kg)</span>
          {editable ? <input type="number" step="0.1" className="isak-input-inline" value={campos.masa_corporal} onChange={e => set('masa_corporal', e.target.value)} /> : <span>{campos.masa_corporal || '-'}</span>}
        </div>
        <div className="isak-tabla-fila">
          <span>Talla (cm)</span>
          {editable ? <input type="number" step="0.1" className="isak-input-inline" value={campos.talla} onChange={e => set('talla', e.target.value)} /> : <span>{campos.talla || '-'}</span>}
        </div>
        <div className="isak-tabla-fila">
          <span>Talla sentado (cm)</span>
          {editable ? <input type="number" step="0.1" className="isak-input-inline" value={campos.talla_sentado} onChange={e => set('talla_sentado', e.target.value)} /> : <span>{campos.talla_sentado || '-'}</span>}
        </div>
        <div className="isak-tabla-fila">
          <span>Envergadura (cm)</span>
          {editable ? <input type="number" step="0.1" className="isak-input-inline" value={campos.envergadura} onChange={e => set('envergadura', e.target.value)} /> : <span>{campos.envergadura || '-'}</span>}
        </div>
      </div>

      {/* ── Pliegues ──────────────────────────────────────────────── */}
      <div className="isak-seccion">
        <h4 className="isak-seccion-titulo">Pliegues (mm)</h4>
        <div className="isak-tabla-header"><span>Pliegue</span><span>Resultado</span></div>
        {[
          ['triceps','Tríceps'], ['subescapular','Subescapular'], ['biceps','Bíceps'],
          ['cresta_iliaca','Cresta Ilíaca'], ['supraespinal','Supraespinal'],
          ['abdominal','Abdominal'], ['muslo','Muslo'], ['pierna','Pierna'],
        ].map(([key, label]) => (
          <div key={key} className="isak-tabla-fila">
            <span>{label}</span>
            {editable ? <input type="number" step="0.1" className="isak-input-inline" value={campos[key]} onChange={e => set(key, e.target.value)} /> : <span>{campos[key] || '-'}</span>}
          </div>
        ))}
        <div className="isak-tabla-fila isak-calculado">
          <span>Suma 6 pliegues (mm)</span><span>{calc.suma6 || 0}</span>
        </div>
        <div className="isak-tabla-fila isak-calculado">
          <span>Suma 8 pliegues (mm)</span><span>{calc.suma8 || 0}</span>
        </div>
      </div>

      {/* ── Perimetros ────────────────────────────────────────────── */}
      <div className="isak-seccion">
        <h4 className="isak-seccion-titulo">Perímetros (cm)</h4>
        <div className="isak-tabla-header"><span>Perímetro</span><span>Resultado</span></div>
        {[
          ['brazo_relajado','Brazo relajado'], ['brazo_flexionado','Brazo flexionado y contraido'],
          ['cintura','Cintura'], ['caderas','Caderas'],
          ['muslo_medio','Muslo medio'], ['pierna_perimetro','Pierna'],
        ].map(([key, label]) => (
          <div key={key} className="isak-tabla-fila">
            <span>{label}</span>
            {editable ? <input type="number" step="0.1" className="isak-input-inline" value={campos[key]} onChange={e => set(key, e.target.value)} /> : <span>{campos[key] || '-'}</span>}
          </div>
        ))}
      </div>

      {/* ── Diametros ─────────────────────────────────────────────── */}
      <div className="isak-seccion">
        <h4 className="isak-seccion-titulo">Diámetros (cm)</h4>
        <div className="isak-tabla-header"><span>Diámetro</span><span>Resultado</span></div>
        {[
          ['humero','Húmero'], ['biastilodeo','Biastilodeo'], ['femur','Fémur'],
        ].map(([key, label]) => (
          <div key={key} className="isak-tabla-fila">
            <span>{label}</span>
            {editable ? <input type="number" step="0.1" className="isak-input-inline" value={campos[key]} onChange={e => set(key, e.target.value)} /> : <span>{campos[key] || '-'}</span>}
          </div>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════
          RESULTADOS
      ══════════════════════════════════════════════════════════ */}

      {/* ── Composicion corporal ──────────────────────────────────── */}
      <ComposicionCorporalSection
        masaGrasa={calc.masaGrasa}
        masaLibre={calc.masaLibre}
        tejidoAdiposo={calc.tejidoAdiposo}
        masaMuscular={calc.masaMuscular}
        masaOsea={calc.masaOsea}
        tejidoResidual={calc.tejidoResidual}
        masaCorporal={n(campos.masa_corporal)}
      />

      {/* ── Indices composicion corporal ──────────────────────────── */}
      <div className="isak-seccion isak-resultados">
        <h4 className="isak-seccion-titulo isak-titulo-resultado">Índices de Composición Corporal</h4>
        <FilaIndice label="Índice adiposo muscular" valor={calc.indAdiposoMuscular || 0} rangos={RANGOS_AM} />
        <FilaIndice label="Índice músculo/óseo" valor={calc.indMusculoOseo || 0} rangos={RANGOS_MO}
          interpretacion="2.34 Muy bajo | 2.44 Bajo | 3.11 Medio | 3.29 Alto | > Muy alto" />
      </div>

      {/* ── Adiposidad ────────────────────────────────────────────── */}
      <AdiposidadSection
        suma6={calc.suma6}
        suma8={calc.suma8}
        triceps={campos.triceps}
        subescapular={campos.subescapular}
        biceps={campos.biceps}
        cresta_iliaca={campos.cresta_iliaca}
        supraespinal={campos.supraespinal}
        abdominal={campos.abdominal}
        muslo={campos.muslo}
        pierna={campos.pierna}
      />

      {/* ── Muscularidad ──────────────────────────────────────────── */}
      <MuscularidadSection
        brazoCor={calc.brazoCor}
        musloCor={calc.musloCor}
        piernaCor={calc.piernaCor}
        difBrazo={calc.difBrazo}
      />

      {/* ── Somatotipo ────────────────────────────────────────────── */}
      <SomatotipoSection
        endomorfia={calc.endomorfia}
        mesomorfia={calc.mesomorfia}
        ectomorfia={calc.ectomorfia}
        somaX={calc.somaX}
        somaY={calc.somaY}
      />

      {/* ── Indices de salud ──────────────────────────────────────── */}
      <IndicesSaludSection
        indCC={calc.indCC}
        indCT={calc.indCT}
        indConicidad={calc.indConicidad}
        imc={calc.imc}
        idg={calc.idg}
        pctTronco={calc.pctTronco}
        pctExtrem={calc.pctExtrem}
      />

      {/* Boton guardar inferior */}
      {editable && (
        <div style={{ textAlign: 'right', marginTop: 16 }}>
          <button className="btn-guardar-sesion" onClick={guardar} disabled={guardado}>
            {guardado ? 'Guardado ✓' : 'Guardar evaluación'}
          </button>
        </div>
      )}
    </div>
  );
}

export default FichaISAK;
